// Games: every game analysed for you, newest first, grouped by day.

import { useState } from "react";
import { api, readJson } from "../app/api.ts";
import { cap, dayLabel, fmtTime, opponentOf, outcomeOf, speedLabel, type GameInfo } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { prefs, savePrefs } from "../app/prefs.ts";
import { navigate } from "../app/router.ts";
import { session } from "../app/session.ts";
import { Pending, ScreenHead, Seg, useLoad } from "../app/ui.tsx";

const SPEED_ORDER = ["classical", "rapid", "blitz", "bullet", "ultrabullet"];

interface GameRow extends GameInfo {
  game_id: string;
  puzzles: number;
  only_moves?: number;
  blunders: number;
  openings?: number;
  solved?: number;
  opening?: string;
}

export function ResultMark({ game }: { game: GameInfo }) {
  const o = outcomeOf(game);
  return (
    <span className={"res " + o} title={o || game.result}>
      {o ? o[0]!.toUpperCase() : "·"}
    </span>
  );
}

export function GamesScreen() {
  // Chosen filters outlive the screen: going into a game and back is a round
  // trip, not a fresh start.
  const [showAll, setShowAll] = useState(prefs.gamesAll === true);
  const [speed, setSpeed] = useState(prefs.gamesSpeed || "all");
  const [library] = useLoad(async () => {
    const data = await readJson(await api("/api/library" + (showAll ? "?all=true" : "")));
    session.analysisVersion = data.analysis_version ?? session.analysisVersion;
    const games: GameRow[] = data.games || [];
    return { games, total: (data.total ?? games.length) as number };
  }, [showAll]);

  const head = (
    <ScreenHead
      title="Games"
      subtitle="Every game analysed for you, newest first."
      actions={
        <button type="button" className="btn" onClick={() => navigate("/analyse")}>
          <Icon name="search" />
          Analyse
        </button>
      }
    />
  );
  if (library.state !== "ready") {
    return (
      <div className="screen">
        {head}
        <Pending value={library} text="Loading your games…" />
      </div>
    );
  }

  const { games, total } = library.data;
  const present = SPEED_ORDER.filter((s) => games.some((g) => g.speed === s));
  // The remembered speed may have nothing in this listing — every rapid game
  // solved, say. Show everything without forgetting the choice.
  const shownSpeed = speed !== "all" && !present.includes(speed) ? "all" : speed;
  const shown = shownSpeed === "all" ? games : games.filter((g) => g.speed === shownSpeed);
  const groups: { label: string; games: GameRow[] }[] = [];
  for (const g of shown) {
    const label = dayLabel(g.played_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.games.push(g);
    else groups.push({ label, games: [g] });
  }
  const chooseAll = (v: boolean) => {
    prefs.gamesAll = v;
    savePrefs();
    setShowAll(v);
  };

  return (
    <div className="screen">
      {head}
      <div className="games-bar">
        <Seg<"waiting" | "all">
          options={[
            { value: "waiting", label: "Waiting" },
            { value: "all", label: `All ${total}` },
          ]}
          value={showAll ? "all" : "waiting"}
          onChange={(v) => chooseAll(v === "all")}
        />
        {present.length > 1 ? (
          <div className="chips">
            {[["all", "All"], ...present.map((s) => [s, cap(s)])].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className="chip"
                aria-pressed={shownSpeed === value}
                onClick={() => {
                  prefs.gamesSpeed = value!;
                  savePrefs();
                  window.scrollTo(0, 0);
                  setSpeed(value!);
                }}
              >
                {label}
                <span className="faint num">{value === "all" ? games.length : games.filter((g) => g.speed === value).length}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {!shown.length ? (
        <div className="empty card">
          <h2>{total ? "Nothing waiting" : "Nothing analysed yet"}</h2>
          <p>
            {total
              ? `All ${total} analysed game${total === 1 ? " is" : "s are"} either clean or already solved.`
              : "Tell Blundermine who you are on Lichess and your games appear here on their own, or paste one to analyse."}
          </p>
          <div className="btn-row">
            {total ? (
              <button type="button" className="btn" onClick={() => chooseAll(true)}>
                Show all
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => navigate("/")}>
                Get started
              </button>
            )}
            <button type="button" className="btn" onClick={() => navigate("/analyse")}>
              Analyse a game
            </button>
          </div>
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.label}>
            <div className="day">{group.label}</div>
            <div className="game-list">
              {group.games.map((g) => (
                <Row key={`${g.game_id}.${g.color}`} g={g} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Row({ g }: { g: GameRow }) {
  const total = g.puzzles + (g.only_moves || 0) + g.blunders + (g.openings || 0);
  return (
    <button type="button" className="game-row" onClick={() => navigate(`/games/${g.game_id}/${g.color}`)}>
      <ResultMark game={g} />
      <div className="game-main">
        <div className="game-opp">{opponentOf(g)}</div>
        <div className="game-sub">
          {/* The opening first: inside a day's group the time is the least
              useful part, so it is what a narrow row cuts. */}
          {[g.opening ? g.opening.split(",")[0] : "", `as ${cap(g.color)}`, speedLabel(g), fmtTime(g.played_at)].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="game-side">
        {g.puzzles ? (
          <span className="count tactic" title="Missed tactics">
            <span className="dot" />
            {g.puzzles}
          </span>
        ) : null}
        {g.only_moves ? (
          <span className="count only_move" title="Only moves">
            <span className="dot" />
            {g.only_moves}
          </span>
        ) : null}
        {g.blunders ? (
          <span className="count blunder" title="Blunders">
            <span className="dot" />
            {g.blunders}
          </span>
        ) : null}
        {/* A game leaves theory at most once, so the mark is a dot with no number. */}
        {g.openings ? (
          <span className="count opening" title="Left theory">
            <span className="dot" />
          </span>
        ) : null}
        {!total ? (
          <span className="count clean">clean</span>
        ) : g.solved ? (
          <span className="count done" title="Solved">
            <Icon name="check" />
            {g.solved}/{total}
          </span>
        ) : null}
        <Icon name="chev" className="chev" />
      </div>
    </button>
  );
}
